import { createContext, useContext, useEffect, useState } from 'react';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

type AdminAuthContextType = {
  admin: AdminUser | null;
  loading: boolean;
  signOut: () => void;
  setAdmin: (admin: AdminUser | null) => void;
};

export const AdminAuthContext = createContext<AdminAuthContextType>({ 
  admin: null, 
  loading: true,
  signOut: () => {},
  setAdmin: () => {}
});

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdminState] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing admin session
    const adminSession = localStorage.getItem('admin_session');
    if (adminSession) {
      try {
        const adminData = JSON.parse(adminSession);
        console.log('Found existing admin session:', adminData);
        setAdminState(adminData);
      } catch (error) {
        console.error('Error parsing admin session:', error);
        localStorage.removeItem('admin_session');
      }
    }
    setLoading(false);
  }, []);

  const setAdmin = (adminData: AdminUser | null) => {
    console.log('Setting admin in context:', adminData);
    setAdminState(adminData);
    if (adminData) {
      localStorage.setItem('admin_session', JSON.stringify(adminData));
    } else {
      localStorage.removeItem('admin_session');
    }
  };

  const signOut = () => {
    console.log('Admin signing out');
    localStorage.removeItem('admin_session');
    setAdminState(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loading, signOut, setAdmin }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};